import React, { useMemo } from "react";
import { t } from "ttag";

import { FormSelect } from "../../../forms/FormSelect";
import { useAppSelector, useDispatchers } from "../../reducers";
import { Dispatchers } from "../dispatchers";

interface IProps {
    prefix: "shipping" | "billing";
    isDisabled?: boolean;
}

export const SavedAddressSelector = (props: IProps): React.ReactNode => {
    const form = useAppSelector((state) => state.checkout.form);
    const savedAddresses = useAppSelector(
        (state) => state.checkout.data.addresses,
    );
    const dispatchers = useDispatchers(Dispatchers);

    const getSavedAddressOptions = useMemo(() => {
        const addresses = savedAddresses
            .map((addr) => {
                // TODO(i18n): Hard-coded comma+space separator assumes English word order. Different locales may need different separators or ordering for address components. Consider using a translatable address format template.
                const descr = [
                    `${addr.title} ${addr.first_name} ${addr.last_name}`,
                    addr.line1,
                    addr.line2,
                    addr.line4,
                    addr.state,
                    addr.postcode,
                ]
                    .filter((item) => {
                        return !!item;
                    })
                    .join(", ")
                    .replace(/\s+/g, " ")
                    .trim();
                return {
                    value: addr.url,
                    label: descr,
                };
            })
            .sort((a, b) => {
                if (a.label === b.label) {
                    return 0;
                }
                return a.label > b.label ? 1 : -1;
            });
        // Add a blank option to the top
        addresses.unshift({
            value: "",
            label: "",
        });
        return addresses;
    }, [savedAddresses]);

    const onSavedAddressChange = (
        event: React.FormEvent<HTMLSelectElement>,
    ) => {
        dispatchers.selectSavedAddress(
            props.prefix,
            savedAddresses,
            event.currentTarget.value,
        );
    };

    if (savedAddresses.length <= 0) {
        return null;
    }

    const elemID = `${props.prefix}_address_url`;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    const addrURL = `${props.prefix}_address_url` as
        | "shipping_address_url"
        | "billing_address_url";
    const value = form[addrURL];

    return (
        <FormSelect
            label={t`Saved Address`}
            labelPlacement="dynamic"
            name={elemID}
            id={elemID}
            value={value}
            onChange={onSavedAddressChange}
            choices={getSavedAddressOptions}
            disabled={props.isDisabled}
        />
    );
};
